import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useVideoTransition } from '../hooks/useVideoTransition';
import { videoConfigs, videoTransitions } from '../config/videos';
import { useLocation, useNavigate } from 'react-router-dom';
import { getOptimizedVideoSrc } from '../utils/videoUtils';
import { useVideoTransitionState } from '../hooks/useVideoTransitionState';

// Global instance tracking
const activeInstance = {
    id: null,
    isDebugEnabled: false
};

const debug = (...args) => {
    if (activeInstance.isDebugEnabled) {
        //console.log('[VideoBackground]', ...args);
    }
};

const VideoBackground = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { videoState, isPlaying } = useVideoTransition();
    const { getTransitionState } = useVideoTransitionState();
    const backgroundRef = useRef(null);
    const transitionVideoRef = useRef(null);
    const instanceIdRef = useRef(`video-instance-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
    const [isInitialized, setIsInitialized] = useState(false);
    const [opacity, setOpacity] = useState(1);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [contentOpacity, setContentOpacity] = useState(1);
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const [isPlayingTransition, setIsPlayingTransition] = useState(false);
    const nextVideoSrcRef = useRef(null);

    // Instance management
    useEffect(() => {
        if (activeInstance.id) {
            debug(`Another instance is already active (${activeInstance.id}). This instance (${instanceIdRef.current}) will not initialize.`);
            return;
        }

        activeInstance.id = instanceIdRef.current;
        debug(`Instance ${instanceIdRef.current} mounted and active.`);
        setIsInitialized(true);

        return () => {
            if (activeInstance.id === instanceIdRef.current) {
                debug(`Instance ${instanceIdRef.current} unmounting and clearing active flag.`);
                activeInstance.id = null;
                setIsInitialized(false);
            }
        };
    }, []);

    // Log when component mounts/unmounts with current pathname
    useEffect(() => {
        if (!isInitialized || activeInstance.id !== instanceIdRef.current) {
            return;
        }

        debug(`Mounted at ${window.location.pathname}`);
        return () => debug(`Unmounting from ${window.location.pathname}`);
    }, [isInitialized]);

    // Function to handle video transitions with fade effect
    const transitionToNewVideo = useCallback(async (newVideoSrc) => {
        if (!backgroundRef.current || isTransitioning) return;
        
        // Get optimized source if needed
        const optimizedSrc = getOptimizedVideoSrc(newVideoSrc);
        debug(`Starting transition to video: ${optimizedSrc} ${optimizedSrc !== newVideoSrc ? '(optimized for mobile)' : ''}`);
            
        // Check if this is a landing to passive screen transition
        const isLandingToPassiveTransition = 
            backgroundRef.current.src.includes('/assets/intro.mp4') && 
            optimizedSrc.includes('/assets/loop.mp4');
            
        if (isLandingToPassiveTransition) {
            debug(`Detected landing to passive transition - using instant transition`);
            
            // Store the new video source
            nextVideoSrcRef.current = optimizedSrc;
            
            const background = backgroundRef.current;
            if (!background) return;
            
            // Find the video config for the current source
            const videoConfig = videoConfigs.find(config => 
                config.src === optimizedSrc
            );
            
            // Create a new video element to preload the passive screen video
            const tempVideo = document.createElement('video');
            tempVideo.muted = true;
            tempVideo.preload = 'auto';
            tempVideo.style.display = 'none';
            tempVideo.src = optimizedSrc;
            
            // Add the temp video to the DOM to ensure preloading works
            document.body.appendChild(tempVideo);
            
            // Preload the video
            tempVideo.load();
            
            // When the video is fully loaded, update the main video
            tempVideo.addEventListener('loadeddata', function onLoaded() {
                // Remove the event listener to prevent multiple calls
                tempVideo.removeEventListener('loadeddata', onLoaded);
                
                debug(`New video preloaded, updating main video`);
                
                // Update the video source
                background.src = optimizedSrc;
                background.loop = videoConfig ? videoConfig.loop : true;
                background.muted = true;
                
                // Play the video
                background.play().then(() => {
                    debug(`New video playing immediately`);
                    
                    // Dispatch event that video source has changed
                    window.dispatchEvent(new CustomEvent('videoSourceChanged', {
                        detail: { src: background.src }
                    }));
                }).catch(err => {
                    debug(`Could not play new video:`, err);
                });
                
                // Remove the temp video from the DOM
                document.body.removeChild(tempVideo);
            });
            
            return; // Exit early for landing to passive transition
        }
        
        // Regular transition with fade for all other cases
        // Store the new video source for after the fade out
        nextVideoSrcRef.current = optimizedSrc;
        
        // Start the transition
        setIsTransitioning(true);
        
        // Fade out current video
        setOpacity(0);
        
        // After fade out completes, update the video source
        setTimeout(() => {
            const background = backgroundRef.current;
            if (!background) return;
            
            debug(`Fade out complete, updating video source to: ${nextVideoSrcRef.current}`);
            
            // Get the current route
            const currentRoute = window.location.pathname;
            
            // Find the expected video config for the current route
            const normalizedCurrentRoute = currentRoute.replace(/\/$/, ''); // Remove trailing slash
            const expectedVideoConfig = videoConfigs.find(v => {
                const normalizedConfigRoute = v.route.replace(/\/$/, ''); // Remove trailing slash
                return normalizedConfigRoute.endsWith('/*') 
                    ? normalizedCurrentRoute.startsWith(normalizedConfigRoute.slice(0, -2))
                    : normalizedConfigRoute === normalizedCurrentRoute;
            });
            
            // Update the video source
            background.src = nextVideoSrcRef.current;
            background.load();
            
            // Set appropriate loop attribute based on video config
            background.loop = expectedVideoConfig ? expectedVideoConfig.loop : true;
            background.autoplay = true;
            background.playsInline = true;
            background.muted = true; // Always muted since we're using separate audio
            
            // Play the video and fade it in
            background.play().then(() => {
                debug(`New video playing, fading in`);
                setOpacity(1);
                
                // After fade in completes, reset transition state
                setTimeout(() => {
                    setIsTransitioning(false);
                    nextVideoSrcRef.current = null;
                    
                    // Dispatch a custom event to notify that the video source has changed
                    debug(`Transition complete, dispatching videoSourceChanged event`);
                    window.dispatchEvent(new CustomEvent('videoSourceChanged', {
                        detail: { src: background.src }
                    }));
                }, 500); // Half a second for fade in
            }).catch(err => {
                debug(`Could not play new video:`, err);
                // Even if play fails, still fade in and complete transition
                setOpacity(1);
                setTimeout(() => {
                    setIsTransitioning(false);
                    nextVideoSrcRef.current = null;
                }, 500);
            });
        }, 500); // Half a second for fade out
    }, [isTransitioning]);

    // Video ended handler
    const handleVideoEnded = useCallback(() => {
        const background = backgroundRef.current;
        if (!background) return;

        // Get the current video source file name
        const currentSrc = background.src;
        const currentVideoFile = currentSrc.split('/').pop();

        // Find the video config for the current source
        const videoConfig = videoConfigs.find(config => 
            config.src.includes(currentVideoFile)
        );

        // Check if the video should loop
        const shouldLoop = videoConfig?.loop || background.loop;

        if (shouldLoop) {
            debug(`Video ended but should loop: ${currentVideoFile}`);
            // Ensure loop is set and restart the video
            background.loop = true;
            background.currentTime = 0;
            background.play().catch(err => {
                debug('Could not restart looping video:', err);
            });
            return;
        }

        // For non-looping videos, check if this is a landing to passive transition
        if (currentVideoFile.includes('intro.mp4') && location.pathname === '/') {
            debug(`Intro video ended, transitioning to passive screen`);
            
            // Find the passive screen video config
            const passiveConfig = videoConfigs.find(config => config.route === '/space');
            if (!passiveConfig) {
                debug(`Could not find passive screen video config`);
                return;
            }

            // Store the next video source
            nextVideoSrcRef.current = passiveConfig.src;

            // Set up seamless transition
            if (typeof window !== 'undefined') {
                window.STILL_LOVED_SEAMLESS_TRANSITION = true;
            }

            // Dispatch the introVideoEnded event
            window.dispatchEvent(new CustomEvent('introVideoEnded'));

            // Update the video source and play immediately
            background.src = passiveConfig.src;
            background.loop = true;
            background.load();
            background.play().catch(err => {
                debug('Could not play passive screen video:', err);
            });
        }
    }, [location.pathname]);

    // Effect to update video source when videoState changes
    useEffect(() => {
        const background = backgroundRef.current;
        if (!background || !isInitialized) return;

        // Get the transition state
        const { isTransitionInProgress } = getTransitionState();
        if (isTransitionInProgress) {
            debug(`Transition in progress, skipping video state change`);
            return;
        }

        // Only proceed if the video source actually needs to change
        const currentVideoFile = background.src.split('/').pop();
        const newVideoFile = videoState.background.split('/').pop();
        
        if (currentVideoFile !== newVideoFile) {
            debug(`Updating video source from ${currentVideoFile} to ${newVideoFile}`);
            
            // Get optimized source if needed
            const optimizedSrc = getOptimizedVideoSrc(videoState.background);
            
            // Update video source
            background.src = optimizedSrc;
            background.load();
            
            // Find the video config for the new source
            const videoConfig = videoConfigs.find(config => 
                config.src === videoState.background
            );
            
            // Set loop based on config
            background.loop = videoConfig?.loop || false;
            
            // Play the video
            if (isPlaying) {
                background.play().catch(err => {
                    debug('Could not play video:', err);
                });
            }
        }
    }, [videoState, isInitialized, isPlaying, getTransitionState]);

    // Initialize video element
    useEffect(() => {
        const background = backgroundRef.current;
        if (!background || isInitialized) return;

        // Get optimized source if needed
        const optimizedSrc = getOptimizedVideoSrc(videoState.background);
        
        // Set initial video source
        background.src = optimizedSrc;
        background.muted = true;
        background.playsInline = true;
        background.load();
        
        // Find the video config for the current source
        const videoConfig = videoConfigs.find(config => 
            config.src === videoState.background
        );
        
        // Set loop based on config
        background.loop = videoConfig?.loop || false;
        
        // Play if needed
        if (isPlaying) {
            background.play().catch(err => {
                debug('Could not play video:', err);
            });
        }

        setIsInitialized(true);
    }, [videoState.background, isPlaying, isInitialized]);

    // Set up video event listeners for both videos
    useEffect(() => {
        const background = backgroundRef.current;
        const transitionVideo = transitionVideoRef.current;
        
        // Use safer event listener approach
        const handleBackgroundEnded = () => handleVideoEnded();
        const handleTransitionEnded = () => handleVideoEnded();
        
        if (background) {
            background.addEventListener('ended', handleBackgroundEnded);
        }
        
        if (transitionVideo) {
            transitionVideo.addEventListener('ended', handleTransitionEnded);
        }
        
        return () => {
            // Proper cleanup of event listeners
            if (background) {
                background.removeEventListener('ended', handleBackgroundEnded);
            }
            
            if (transitionVideo) {
                transitionVideo.removeEventListener('ended', handleTransitionEnded);
            }
        };
    }, [handleVideoEnded]);

    // Check for route changes and update the video source if needed
    useEffect(() => {
        // Skip if this is not the active instance
        if (!isInitialized || activeInstance.id !== instanceIdRef.current) {
            return;
        }

        // Skip if a transition is already in progress
        if (typeof window !== 'undefined' && window.STILL_LOVED_TRANSITION_IN_PROGRESS) {
            debug(`Transition already in progress, skipping route change handler`);
            return;
        }

        const background = backgroundRef.current;
        if (!background) return;
        
        debug(`Route changed to ${location.pathname}`);
        
        // Get the current route
        const currentRoute = location.pathname;
        
        // Find the expected video config for the current route
        const normalizedCurrentRoute = currentRoute.replace(/\/$/, ''); // Remove trailing slash
        const expectedVideoConfig = videoConfigs.find(v => {
            const normalizedConfigRoute = v.route.replace(/\/$/, ''); // Remove trailing slash
            return normalizedConfigRoute.endsWith('/*') 
                ? normalizedCurrentRoute.startsWith(normalizedConfigRoute.slice(0, -2))
                : normalizedConfigRoute === normalizedCurrentRoute;
        });
        
        if (!expectedVideoConfig) {
            debug(`No video config found for route ${currentRoute}`);
            return;
        }
        
        // Check if the current video source matches the expected video source
        const currentVideoFile = background.src.split('/').pop();
        const expectedVideoFile = expectedVideoConfig.src.split('/').pop();
        
        if (currentVideoFile !== expectedVideoFile) {
            debug(`Updating video source for route ${currentRoute}`);
            debug(`Current video: ${background.src}`);
            debug(`Expected video: ${expectedVideoConfig.src}`);
            
            // Use the transition function to smoothly change the video
            transitionToNewVideo(expectedVideoConfig.src);
        } else {
            // Even if the source is the same, ensure the loop attribute is set correctly
            background.loop = expectedVideoConfig.loop;
        }
    }, [location.pathname, isInitialized, transitionToNewVideo]);

    // Listen for navigateToLanding event
    useEffect(() => {
        const handleNavigateToLanding = (event) => {
            // Skip if this is not the active instance
            if (!isInitialized || activeInstance.id !== instanceIdRef.current) {
                return;
            }
            
            // Only log in development
            if (process.env.NODE_ENV === 'development') {
                debug('Received navigateToLanding event, setting up intro video');
            }
            
            // Find the intro video config
            const introVideoConfig = videoConfigs.find(v => v.route === '/');
            if (!introVideoConfig) {
                debug('Could not find intro video config');
                return;
            }
            
            // Get the background element
            const background = backgroundRef.current;
            if (!background) {
                debug('Could not find background video element');
                return;
            }
            
            // Force update the video source
            const introVideoSrc = event.detail?.src || introVideoConfig.src;
            
            // Only log in development
            if (process.env.NODE_ENV === 'development') {
                debug('Forcing intro video source update:', introVideoSrc);
            }
            
            // Use the transition function to smoothly change the video
            transitionToNewVideo(introVideoSrc);
            
            // Ensure loop attribute is set correctly
            if (background && introVideoConfig) {
                background.loop = introVideoConfig.loop;
            }
        };
        
        window.addEventListener('navigateToLanding', handleNavigateToLanding);
        
        return () => {
            window.removeEventListener('navigateToLanding', handleNavigateToLanding);
        };
    }, [isInitialized, transitionToNewVideo]);

    // Listen for content fade out event
    useEffect(() => {
        const handleFadeOutContent = (event) => {
            // Skip if this is not the active instance
            if (!isInitialized || activeInstance.id !== instanceIdRef.current) {
                return;
            }
            
            const { targetPath } = event.detail;
            debug(`Received fade out content event for path: ${targetPath}`);
            
            // Add the content-fading-out class to the body
            document.body.classList.add('content-fading-out');
            
            // Fade out the content
            setContentOpacity(0);
            
            // After content fade out, start the transition
            setTimeout(() => {
                debug(`Content fade out complete, starting transition to: ${targetPath}`);
                
                // Find the transition video for the current route to target route
                const currentPath = window.location.pathname;
                const transition = videoTransitions.find(t => {
                    const fromMatches = t.from === currentPath;
                    const toMatches = t.to.endsWith('/*') 
                        ? targetPath.startsWith(t.to.slice(0, -2))
                        : t.to === targetPath;
                    return fromMatches && toMatches;
                });
                
                // If no transition is defined, use the default navigation
                if (!transition) {
                    debug(`No transition defined for ${currentPath} to ${targetPath}, using default navigation`);
                    
                    // Remove the content-fading-out class from the body
                    document.body.classList.remove('content-fading-out');
                    
                    // Reset content opacity
                    setContentOpacity(1);
                    
                    navigate(targetPath);
                    return;
                }
                
                debug(`Found transition video: ${transition.src}`);
                
                // Use the transition video element instead of the background video
                const transitionVideo = transitionVideoRef.current;
                if (!transitionVideo) {
                    debug('Could not find transition video element');
                    
                    // Remove the content-fading-out class from the body
                    document.body.classList.remove('content-fading-out');
                    
                    // Reset content opacity
                    setContentOpacity(1);
                    
                    navigate(targetPath);
                    return;
                }
                
                debug(`Playing transition video: ${transition.src}`);
                
                // Mark that we're playing a transition video
                setIsPlayingTransition(true);
                
                // Set the transition video source
                transitionVideo.src = transition.src;
                transitionVideo.loop = false; // Ensure the transition video doesn't loop
                transitionVideo.load();
                
                // Always mute videos since we're using separate audio
                transitionVideo.muted = true;
                
                // Set the pending navigation
                setPendingNavigation(targetPath);
                
                // Make the transition video visible
                transitionVideo.style.display = 'block';
                
                // Play the transition video
                transitionVideo.play().catch(err => {
                    debug(`Could not play transition video:`, err);
                    
                    // Remove the content-fading-out class from the body
                    document.body.classList.remove('content-fading-out');
                    
                    // Reset content opacity
                    setContentOpacity(1);
                    
                    // Reset transition state
                    setIsPlayingTransition(false);
                    
                    // Hide the transition video
                    transitionVideo.style.display = 'none';
                    
                    // If play fails, navigate directly
                    navigate(targetPath);
                });
            }, 500); // Half a second for content fade out
        };
        
        window.addEventListener('stillLovedFadeOutContent', handleFadeOutContent);
        
        return () => {
            window.removeEventListener('stillLovedFadeOutContent', handleFadeOutContent);
        };
    }, [isInitialized, navigate]);

    // Add debugging for the current video source
    useEffect(() => {
        if (!isInitialized || activeInstance.id !== instanceIdRef.current) {
            return;
        }

        const background = backgroundRef.current;
        if (!background) return;
        
        debug(`Current video source: ${background.src}`);
        debug(`Is playing transition: ${isPlayingTransition}`);
        debug(`Pending navigation: ${pendingNavigation}`);
    }, [isPlayingTransition, pendingNavigation, isInitialized]);

    // Add user interaction detection
    useEffect(() => {
        const handleUserInteraction = () => {
            if (typeof window !== 'undefined' && !window.STILL_LOVED_USER_INTERACTED) {
                debug(`First user interaction detected`);
                window.STILL_LOVED_USER_INTERACTED = true;
                
                // If user previously set unmuted, apply it now
                if (window.STILL_LOVED_VIDEO_UNMUTED || sessionStorage.getItem('videoUnmuted') === 'true') {
                    const videos = document.querySelectorAll('video');
                    videos.forEach(video => {
                        video.muted = false;
                        video.volume = 1.0;
                    });
                }
            }
        };

        ['click', 'touchstart', 'keydown'].forEach(event => {
            document.addEventListener(event, handleUserInteraction, { once: true });
        });

        return () => {
            ['click', 'touchstart', 'keydown'].forEach(event => {
                document.removeEventListener(event, handleUserInteraction);
            });
        };
    }, []);

    return (
        <div className="fixed inset-0 z-0 overflow-hidden bg-black">
            <div 
                className="absolute inset-0 bg-black transition-opacity duration-500"
                style={{ opacity: 1 - opacity }}
            />
            <video
                id="background-video"
                ref={backgroundRef}
                className="absolute inset-0 w-full h-full object-cover"
                style={{ 
                    opacity,
                    zIndex: 0,
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    visibility: 'visible',
                    backgroundColor: 'black',
                    transition: (typeof window !== 'undefined' && 
                                (window.STILL_LOVED_SEAMLESS_TRANSITION || 
                                 window.STILL_LOVED_TRANSITION_IN_PROGRESS)) ? 
                               'none' : 'opacity 500ms'
                }}
                autoPlay
                playsInline
                muted
                src={getOptimizedVideoSrc(videoState.background)}
                loop={videoConfigs.find(v => v.src === videoState.background)?.loop ?? true}
            />
            
            {/* Separate video element for transition videos */}
            <video
                id="transition-video"
                ref={transitionVideoRef}
                className="absolute inset-0 w-full h-full object-cover"
                style={{ 
                    zIndex: 999,
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: isPlayingTransition ? 'block' : 'none',
                    visibility: 'visible'
                }}
                autoPlay
                playsInline
                muted
                src={getOptimizedVideoSrc(videoState.transition)}
            />
        </div>
    );
};

export default VideoBackground; 